const CART_DRAWER_DEFAULT_SINGLE_PLAN_ID = "cmjsqyn5t1cgkxf3i4zyuzzgw";
const CART_DRAWER_DEFAULT_BUNDLE_PLAN_ID = "cmkfntw630aav1zkr0o0jgpbl";
const CART_DRAWER_DEFAULT_FALLBACK_APP_ID = "5859381";
const CART_DRAWER_DEFAULT_EXISTING_BUNDLE_POLICY = "separate";
const CART_DRAWER_DEFAULT_MIXED_MODE_POLICY = "separate";
const CART_DRAWER_DEFAULT_AUTO_CREATE_MODE = "same-mode-only";
const CART_DRAWER_DEFAULT_FREQUENCY_CONFLICT_POLICY = "first-added";
const CART_DRAWER_DEFAULT_STRICT_MODE_CONSENT = true;
const CART_DRAWER_DEFAULT_BUNDLE_PARENT_HANDLES = [
  "avanelle-cognitive-prime",
  "creatine-gummies-container-active",
  "creatine-gummies-container-elite",
];
const CART_DRAWER_DEBUG_KEY = "avCartSyncDebug";
const CART_DRAWER_DEBUG_VERBOSE_KEY = "avCartSyncDebugVerbose";
const CART_DRAWER_FREQUENCY_SELECTION_TTL_MS = 15000;

const getCartDrawerDefaultSubscriptionConfig = () => ({
  singlePlanId: CART_DRAWER_DEFAULT_SINGLE_PLAN_ID,
  bundlePlanId: CART_DRAWER_DEFAULT_BUNDLE_PLAN_ID,
  activePlanId: CART_DRAWER_DEFAULT_SINGLE_PLAN_ID,
  fallbackAppId: CART_DRAWER_DEFAULT_FALLBACK_APP_ID,
  existingBundlePolicy: CART_DRAWER_DEFAULT_EXISTING_BUNDLE_POLICY,
  mixedModePolicy: CART_DRAWER_DEFAULT_MIXED_MODE_POLICY,
  autoCreateMode: CART_DRAWER_DEFAULT_AUTO_CREATE_MODE,
  frequencyConflictPolicy: CART_DRAWER_DEFAULT_FREQUENCY_CONFLICT_POLICY,
  strictModeConsent: CART_DRAWER_DEFAULT_STRICT_MODE_CONSENT,
  bundleParentHandles: [...CART_DRAWER_DEFAULT_BUNDLE_PARENT_HANDLES],
});

class CartDrawerSection extends HTMLElement {
  cartUpdateUnsubscriber = undefined;
  constructor() {
    super();

    this.cartType = this.dataset.cartType;
    this.drawerClass = "wt-cart__drawer";
    this.drawer = this.querySelector(`.${this.drawerClass}`);
    this.classDrawerActive = `${this.drawerClass}--open`;
    this.pageOverlayClass = "page-overlay-cart";
    this.activeOverlayBodyClass = `${this.pageOverlayClass}-on`;
    this.body = document.body;
    this.triggerQuery = [
      `.wt-cart__trigger`,
      `.wt-cart__back-link`,
      `.${this.pageOverlayClass}`,
    ].join(", ");
    this.triggers = () => document.querySelectorAll(this.triggerQuery);
    this.isOpen = false;
    this.isCartPage = window.location.pathname === window.routes.cart_url;
    this.closeButton = () => this.querySelector(".wt-cart__drawer__close");
    this.mainTrigger = document.querySelector(".wt-cart__trigger");
    this.toggleEelements = () =>
      this.querySelectorAll(this.dataset.toggleTabindex);
    this.reserveTimerInterval = undefined;
    this.isRecoUpdating = false;
    this.isAutoshipUpdating = false;
    this.productJsonCache = new Map();
    this.autoshipOptionsRequestId = 0;
    this.lastFrequencySelection = {
      frequencyKey: "",
      groupId: "",
      updatedAt: 0,
    };
    this.boundActionTargets = new WeakSet();
    this.boundCartActionClick = this.handleCartActionClick.bind(this);
    this.boundCartActionChange = this.handleCartActionChange.bind(this);
  }

  isDebugEnabled() {
    return window.__avCartSyncDebug === true || window.__mainCartAutoshipDebug === true;
  }

  isDebugVerboseEnabled() {
    return this.isDebugEnabled() && window.__avCartSyncDebugVerbose === true;
  }

  debugLog(...args) {
    if (!this.isDebugVerboseEnabled()) return;
    console.log("[CartDrawerAutoship]", ...args);
  }

  debugWarn(...args) {
    if (!this.isDebugEnabled()) return;
    console.warn("[CartDrawerAutoship]", ...args);
  }

  debugIssue(problem, cause, meta = {}) {
    if (!this.isDebugEnabled()) return;
    const normalizedProblem = String(problem || "unknown").trim() || "unknown";
    const normalizedCause = String(cause || "unknown").trim() || "unknown";
    if (meta && typeof meta === "object" && Object.keys(meta).length > 0) {
      console.warn(
        "[CartDrawerAutoship]",
        `${normalizedProblem} - ${normalizedCause}`,
        meta,
      );
      return;
    }
    console.warn("[CartDrawerAutoship]", `${normalizedProblem} - ${normalizedCause}`);
  }

  rememberFrequencySelection(frequencyKey = "", groupId = "") {
    this.lastFrequencySelection = {
      frequencyKey: this.normalizeFrequencyKey(frequencyKey),
      groupId: String(groupId || "").trim(),
      updatedAt: Date.now(),
    };
  }

  clearFrequencySelection() {
    this.lastFrequencySelection = {
      frequencyKey: "",
      groupId: "",
      updatedAt: 0,
    };
  }

  getRecentFrequencySelection() {
    const state = this.lastFrequencySelection || {};
    const updatedAt = Number(state.updatedAt || 0);
    if (!updatedAt || Date.now() - updatedAt > CART_DRAWER_FREQUENCY_SELECTION_TTL_MS) {
      this.clearFrequencySelection();
      return null;
    }
    const frequencyKey = this.normalizeFrequencyKey(state.frequencyKey || "");
    if (!frequencyKey) {
      this.clearFrequencySelection();
      return null;
    }
    return {
      frequencyKey,
      groupId: String(state.groupId || "").trim(),
      updatedAt,
    };
  }

  parseBooleanFlag(value) {
    if (value === true || value === 1) return true;
    if (value === false || value === 0 || value === null || value === undefined) {
      return false;
    }
    const normalizedValue = String(value)
      .trim()
      .toLowerCase();
    if (!normalizedValue) return false;
    return (
      normalizedValue === "true" ||
      normalizedValue === "1" ||
      normalizedValue === "yes" ||
      normalizedValue === "on"
    );
  }

  normalizePurchaseMode(value, fallback = "") {
    const normalizedValue = String(value || "")
      .trim()
      .toLowerCase();
    if (normalizedValue === "sub") return "sub";
    if (normalizedValue === "otp") return "otp";
    const normalizedFallback = String(fallback || "")
      .trim()
      .toLowerCase();
    if (normalizedFallback === "sub" || normalizedFallback === "otp") {
      return normalizedFallback;
    }
    return "";
  }

  isStrictModeConsentEnabled(config) {
    if (!config || typeof config !== "object") {
      return CART_DRAWER_DEFAULT_STRICT_MODE_CONSENT;
    }
    if (config.strictModeConsent === undefined || config.strictModeConsent === null) {
      return CART_DRAWER_DEFAULT_STRICT_MODE_CONSENT;
    }
    if (typeof config.strictModeConsent === "boolean") {
      return config.strictModeConsent;
    }
    return this.parseBooleanFlag(config.strictModeConsent);
  }

  resolveExplicitPurchaseModeFromControl(controlButton) {
    if (!controlButton) return "";
    const directMode = this.normalizePurchaseMode(
      controlButton.dataset?.purchaseMode ||
        controlButton.dataset?.mode ||
        controlButton.dataset?.cartPurchaseMode,
    );
    if (directMode) return directMode;
    const scopedNode = controlButton.closest(
      "[data-purchase-mode],[data-cart-purchase-mode],[data-mode]",
    );
    if (!scopedNode) return "";
    return this.normalizePurchaseMode(
      scopedNode.getAttribute("data-purchase-mode") ||
        scopedNode.getAttribute("data-cart-purchase-mode") ||
        scopedNode.getAttribute("data-mode"),
    );
  }

  isBundleParentLineItem(lineItem) {
    return this.parseBooleanFlag(lineItem?.properties?._bundle_parent);
  }

  normalizeHandle(value) {
    return String(value || "")
      .trim()
      .toLowerCase();
  }

  getLineHandle(lineItem) {
    return this.normalizeHandle(
      lineItem?.handle ||
        lineItem?.product_handle ||
        this.extractProductHandleFromUrl(lineItem?.url || ""),
    );
  }

  getBundleParentHandlesFromConfig(config) {
    const handles = Array.isArray(config?.bundleParentHandles)
      ? config.bundleParentHandles
          .map((handle) => this.normalizeHandle(handle))
          .filter(Boolean)
      : [];
    return handles.length > 0
      ? handles
      : [...CART_DRAWER_DEFAULT_BUNDLE_PARENT_HANDLES];
  }

  isBundleParentItem(lineItem, parentHandles = []) {
    if (this.isBundleParentLineItem(lineItem)) return true;
    const properties = lineItem?.properties || {};
    const bundleId = String(properties?._bundle_id || "").trim();
    const handle = this.getLineHandle(lineItem);
    if (!bundleId) {
      return Boolean(handle && parentHandles.includes(handle));
    }

    const hasParentRangeHint =
      String(properties?._bundle_min || "").trim() !== "" ||
      String(properties?._bundle_max || "").trim() !== "";
    if (hasParentRangeHint) return true;

    return Boolean(handle && parentHandles.includes(handle));
  }

  parseIntervalCountFromPlanName(planName) {
    const normalizedName = String(planName || "").toLowerCase();
    if (!normalizedName) return { count: 0, unit: "" };
    const directMatch = normalizedName.match(/(\d+)\s*(day|week|month)s?/i);
    if (directMatch) {
      return {
        count: Number(directMatch[1] || 0),
        unit: this.normalizeIntervalUnit(directMatch[2] || "", ""),
      };
    }
    if (
      normalizedName.includes("every 2 month") ||
      normalizedName.includes("2 month") ||
      normalizedName.includes("bi month") ||
      normalizedName.includes("bi-month")
    ) {
      return { count: 2, unit: "month" };
    }
    if (normalizedName.includes("month")) return { count: 1, unit: "month" };
    if (normalizedName.includes("week")) return { count: 1, unit: "week" };
    if (normalizedName.includes("day")) return { count: 1, unit: "day" };
    return { count: 0, unit: "" };
  }

  getPlanFrequencyLabel(plan) {
    if (!plan || !Array.isArray(plan.options)) {
      return String(plan?.name || "").trim();
    }
    const frequencyOption = plan.options.find((option) => {
      const optionName = String(option?.name || "").trim().toLowerCase();
      return optionName === "frequency";
    });
    const optionValue = String(frequencyOption?.value || "").trim();
    if (optionValue) return optionValue;
    return String(plan?.name || "").trim();
  }

  getPlanIntervalData(plan, fallbackUnit = "month") {
    const policy = plan?.delivery_policy || {};
    let count = Number(policy.interval_count || 0);
    let unit = this.normalizeIntervalUnit(policy.interval || "", fallbackUnit);

    if (!Number.isFinite(count) || count <= 0) {
      const parsedFromFrequency = this.parseIntervalCountFromPlanName(
        this.getPlanFrequencyLabel(plan),
      );
      count = Number(parsedFromFrequency.count || 0);
      if (!unit) {
        unit = this.normalizeIntervalUnit(parsedFromFrequency.unit || "", unit);
      }
    }
    if (!Number.isFinite(count) || count <= 0) {
      const parsedFromName = this.parseIntervalCountFromPlanName(plan?.name || "");
      count = Number(parsedFromName.count || 0);
      if (!unit) {
        unit = this.normalizeIntervalUnit(parsedFromName.unit || "", unit);
      }
    }
    if (!unit) {
      unit = this.normalizeIntervalUnit(fallbackUnit || "month", "month");
    }

    return {
      count: Number.isFinite(count) && count > 0 ? count : 0,
      unit: unit || fallbackUnit,
    };
  }

  getFrequencyKeyFromInterval(intervalData, fallbackPlanId = 0) {
    const count = Number(intervalData?.count || 0);
    const unit = this.normalizeIntervalUnit(intervalData?.unit || "", "");
    if (count > 0 && unit) {
      return `${unit}:${count}`;
    }
    const fallbackId = Number(fallbackPlanId || 0);
    return fallbackId > 0 ? `plan:${fallbackId}` : "";
  }

  parseFrequencyKey(frequencyKey) {
    const key = String(frequencyKey || "").trim().toLowerCase();
    if (!key) return { key: "", count: 0, unit: "" };
    const directMatch = key.match(/^([a-z]+)\s*:\s*(\d+)$/);
    if (directMatch) {
      return {
        key: `${this.normalizeIntervalUnit(directMatch[1], "")}:${Number(
          directMatch[2] || 0,
        )}`,
        count: Number(directMatch[2] || 0),
        unit: this.normalizeIntervalUnit(directMatch[1], ""),
      };
    }
    return { key, count: 0, unit: "" };
  }

  normalizeFrequencyKey(frequencyKey) {
    const rawKey = String(frequencyKey || "")
      .trim()
      .toLowerCase();
    if (!rawKey) return "";
    const parsedKey = this.parseFrequencyKey(rawKey);
    return parsedKey.key || rawKey;
  }

  getFrequencyKeyAliases(frequencyKey) {
    const normalizedKey = this.normalizeFrequencyKey(frequencyKey);
    const aliases = new Set();
    if (!normalizedKey) return aliases;

    aliases.add(normalizedKey);
    const parsed = this.parseFrequencyKey(normalizedKey);
    const count = Number(parsed.count || 0);
    const unit = this.normalizeIntervalUnit(parsed.unit || "", "");
    if (count > 0) {
      if (unit === "month") {
        aliases.add(`week:${count * 4}`);
      } else if (unit === "week" && count % 4 === 0) {
        aliases.add(`month:${count / 4}`);
      }
    }

    return aliases;
  }

  isFrequencyKeyMatch(firstKey, secondKey) {
    const firstAliases = this.getFrequencyKeyAliases(firstKey);
    const secondAliases = this.getFrequencyKeyAliases(secondKey);
    if (firstAliases.size === 0 || secondAliases.size === 0) return false;
    for (const alias of firstAliases) {
      if (secondAliases.has(alias)) return true;
    }
    return false;
  }

  normalizeGroupIdentifier(value) {
    return String(value || "")
      .trim()
      .toLowerCase();
  }

  isGroupIdMatch(candidateGroupId, targetGroupId) {
    const candidate = this.normalizeGroupIdentifier(candidateGroupId);
    const target = this.normalizeGroupIdentifier(targetGroupId);
    if (!target) return true;
    if (!candidate) return true;
    return candidate.includes(target) || target.includes(candidate);
  }

  getLineGroupId(lineItem) {
    return String(
      lineItem?.selling_plan_allocation?.selling_plan?.group_id ||
        lineItem?.selling_plan_allocation?.selling_plan?.selling_plan_group_id ||
        lineItem?.selling_plan_allocation?.selling_plan_group_id ||
        "",
    ).trim();
  }

  getLineFrequencyData(lineItem) {
    const plan = lineItem?.selling_plan_allocation?.selling_plan;
    const intervalData = this.getPlanIntervalData(plan, "month");
    return {
      frequencyKey: this.getFrequencyKeyFromInterval(intervalData, plan?.id),
      frequencyLabel: this.getPlanFrequencyLabel(plan),
      groupId: this.getLineGroupId(lineItem),
      count: Number(intervalData.count || 0),
      unit: this.normalizeIntervalUnit(intervalData.unit || "month", "month"),
    };
  }

  getFrequencySortWeight(count, unit) {
    const safeCount = Number(count || 0);
    if (!Number.isFinite(safeCount) || safeCount <= 0) {
      return Number.MAX_SAFE_INTEGER;
    }
    const normalizedUnit = this.normalizeIntervalUnit(unit || "", "");
    const multiplier =
      normalizedUnit === "day"
        ? 1
        : normalizedUnit === "week"
          ? 7
          : normalizedUnit === "month"
            ? 30
            : normalizedUnit === "year"
              ? 365
              : 10000;
    return safeCount * multiplier;
  }

  getSubscriptionConfigFromDom() {
    const defaultConfig = getCartDrawerDefaultSubscriptionConfig();
    const configElement =
      this.querySelector("[data-cart-drawer-subscription-config]") ||
      document.querySelector("[data-main-cart-subscription-config]") ||
      document.querySelector("[data-cart-drawer-subscription-config]");
    if (!configElement) {
      return defaultConfig;
    }
    const rawConfig = String(configElement.textContent || "").trim();
    if (!rawConfig) {
      return defaultConfig;
    }
    try {
      const parsed = JSON.parse(rawConfig);
      const singlePlanId = String(
        parsed?.singlePlanId || defaultConfig.singlePlanId || "",
      ).trim();
      const bundlePlanId = String(
        parsed?.bundlePlanId || defaultConfig.bundlePlanId || "",
      ).trim();
      const activePlanId = String(
        parsed?.activePlanId || singlePlanId || defaultConfig.activePlanId || "",
      ).trim();
      const fallbackAppId = String(
        parsed?.fallbackAppId || defaultConfig.fallbackAppId || "5859381",
      ).trim();
      const existingBundlePolicy = String(
        parsed?.existingBundlePolicy || defaultConfig.existingBundlePolicy || "separate",
      )
        .trim()
        .toLowerCase();
      const mixedModePolicy = String(
        parsed?.mixedModePolicy || defaultConfig.mixedModePolicy || "separate",
      )
        .trim()
        .toLowerCase();
      const autoCreateMode = String(
        parsed?.autoCreateMode || defaultConfig.autoCreateMode || "same-mode-only",
      )
        .trim()
        .toLowerCase();
      const frequencyConflictPolicy = String(
        parsed?.frequencyConflictPolicy ||
          defaultConfig.frequencyConflictPolicy ||
          "first-added",
      )
        .trim()
        .toLowerCase();
      const strictModeConsentRaw =
        parsed?.strictModeConsent !== undefined
          ? parsed.strictModeConsent
          : defaultConfig.strictModeConsent;
      const strictModeConsent =
        strictModeConsentRaw === undefined
          ? CART_DRAWER_DEFAULT_STRICT_MODE_CONSENT
          : this.parseBooleanFlag(strictModeConsentRaw);
      const bundleParentHandles = Array.isArray(parsed?.bundleParentHandles)
        ? parsed.bundleParentHandles
            .map((handle) => this.normalizeHandle(handle))
            .filter(Boolean)
        : [];
      return {
        singlePlanId,
        bundlePlanId,
        activePlanId,
        fallbackAppId,
        existingBundlePolicy: existingBundlePolicy || "separate",
        mixedModePolicy: mixedModePolicy || "separate",
        autoCreateMode: autoCreateMode || "same-mode-only",
        frequencyConflictPolicy: frequencyConflictPolicy || "first-added",
        strictModeConsent,
        bundleParentHandles:
          bundleParentHandles.length > 0
            ? bundleParentHandles
            : [...defaultConfig.bundleParentHandles],
      };
    } catch (error) {
      return defaultConfig;
    }
  }

  getAutoshipContext(cart) {
    const config = this.getSubscriptionConfigFromDom();
    const parentHandles = this.getBundleParentHandlesFromConfig(config);
    const hasBundleParent = (cart?.items || []).some((item) =>
      this.isBundleParentItem(item, parentHandles),
    );
    const autoshipLines = (cart?.items || []).filter(
      (item) => Number(item?.quantity || 0) > 0 && item?.selling_plan_allocation,
    );
    const preferredAutoshipLine = hasBundleParent
      ? autoshipLines.find((item) => this.isBundleParentItem(item, parentHandles)) ||
        autoshipLines[0]
      : autoshipLines[0];
    const lineGroupId = this.getLineGroupId(preferredAutoshipLine);
    const preferredPlanGroupId = String(
      (hasBundleParent ? config.bundlePlanId : config.singlePlanId) || "",
    ).trim();
    let groupId = String(lineGroupId || preferredPlanGroupId || "").trim();
    if (!groupId) {
      groupId = String(
        config.activePlanId || "",
      ).trim();
    }
    if (!groupId) {
      groupId = String(
        config.singlePlanId || config.bundlePlanId || config.activePlanId || "",
      ).trim();
    }
    this.debugLog("Autoship context resolved", {
      hasBundleParent,
      groupId: String(groupId || "").trim(),
      preferredPlanGroupId,
      lineGroupId: String(lineGroupId || "").trim(),
      autoshipLineCount: autoshipLines.length,
      singlePlanId: config.singlePlanId || "",
      bundlePlanId: config.bundlePlanId || "",
      activePlanId: config.activePlanId || "",
      parentHandles,
    });
    return {
      config,
      groupId: String(groupId || "").trim(),
      hasBundleParent,
    };
  }

  getGroupMatchedOptions(options, desiredGroupId = "", fallbackAppId = "") {
    const safeOptions = Array.isArray(options) ? options : [];
    if (safeOptions.length === 0) return [];
    const normalizedDesiredGroup = this.normalizeGroupIdentifier(desiredGroupId);
    const normalizedFallbackAppId = this.normalizeGroupIdentifier(fallbackAppId);
    if (normalizedDesiredGroup) {
      const groupMatched = safeOptions.filter((optionData) =>
        this.isGroupIdMatch(optionData?.groupId, normalizedDesiredGroup),
      );
      if (groupMatched.length > 0) return groupMatched;
    }
    if (normalizedFallbackAppId) {
      const fallbackMatched = safeOptions.filter((optionData) => {
        const optionGroup = this.normalizeGroupIdentifier(optionData?.groupId);
        return optionGroup.includes(normalizedFallbackAppId);
      });
      if (fallbackMatched.length > 0) return fallbackMatched;
    }
    return safeOptions;
  }

  getOptionIntervalData(optionData) {
    const directCount = Number(optionData?.count || 0);
    const directUnit = this.normalizeIntervalUnit(optionData?.unit || "", "");
    if (directCount > 0 && directUnit) {
      return {
        count: directCount,
        unit: directUnit,
        frequencyKey: this.normalizeFrequencyKey(`${directUnit}:${directCount}`),
      };
    }

    const rawFrequencyKey = this.normalizeFrequencyKey(
      optionData?.frequencyKey || optionData?.key || "",
    );
    const parsedFrequency = this.parseFrequencyKey(rawFrequencyKey);
    const parsedCount = Number(parsedFrequency.count || 0);
    const parsedUnit = this.normalizeIntervalUnit(parsedFrequency.unit || "", "");
    if (parsedCount > 0 && parsedUnit) {
      return {
        count: parsedCount,
        unit: parsedUnit,
        frequencyKey: this.normalizeFrequencyKey(`${parsedUnit}:${parsedCount}`),
      };
    }

    const parsedFromLabel = this.parseIntervalCountFromPlanName(
      `${optionData?.frequencyLabel || ""} ${optionData?.name || ""}`,
    );
    const labelCount = Number(parsedFromLabel.count || 0);
    const labelUnit = this.normalizeIntervalUnit(parsedFromLabel.unit || "", "");
    if (labelCount > 0 && labelUnit) {
      return {
        count: labelCount,
        unit: labelUnit,
        frequencyKey: this.normalizeFrequencyKey(`${labelUnit}:${labelCount}`),
      };
    }

    return {
      count: 0,
      unit: "",
      frequencyKey: rawFrequencyKey,
    };
  }

  getOptionFrequencyKey(optionData) {
    const optionInterval = this.getOptionIntervalData(optionData);
    if (optionInterval.frequencyKey) return optionInterval.frequencyKey;
    return this.normalizeFrequencyKey(optionData?.frequencyKey || optionData?.key || "");
  }

  findFrequencyOption(
    options,
    desiredFrequencyKey,
    desiredGroupId = "",
    fallbackAppId = "",
  ) {
    const safeOptions = Array.isArray(options) ? options : [];
    if (safeOptions.length === 0) return null;
    const normalizedDesiredKey = this.normalizeFrequencyKey(desiredFrequencyKey);
    const desiredAliases = this.getFrequencyKeyAliases(normalizedDesiredKey);
    const scopedOptions = this.getGroupMatchedOptions(
      safeOptions,
      desiredGroupId,
      fallbackAppId,
    );
    if (!normalizedDesiredKey) {
      return scopedOptions[0] || safeOptions[0] || null;
    }
    const exactScopedMatch = scopedOptions.find(
      (optionData) =>
        desiredAliases.has(
          this.getOptionFrequencyKey(optionData),
        ),
    );
    if (exactScopedMatch) return exactScopedMatch;
    const exactGlobalMatch = safeOptions.find(
      (optionData) =>
        desiredAliases.has(
          this.getOptionFrequencyKey(optionData),
        ),
    );
    if (exactGlobalMatch) return exactGlobalMatch;
    return scopedOptions[0] || safeOptions[0] || null;
  }

  connectedCallback() {
    if (this.cartType === "page" || this.isCartPage) {
      this.bindCartActionDelegates(document);
      document.addEventListener("cart-drawer:refresh", (e) =>
        this.refreshCartDrawer(e),
      );
      this.setupReserveTimer();
      this.hydrateAutoshipFrequencyOptions();
      return;
    }

    this.init();
    setTabindex(this.toggleEelements(), "-1");
    this.setupReserveTimer();
    this.hydrateAutoshipFrequencyOptions();
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      if (this.isOpen) {
        setTabindex(this.toggleEelements(), "0");
        this.closeButton().focus();
      }
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
    if (this.boundActionTargets.has(this)) {
      this.removeEventListener("click", this.boundCartActionClick);
      this.removeEventListener("change", this.boundCartActionChange);
      this.boundActionTargets.delete(this);
    }
    if (this.boundActionTargets.has(document)) {
      document.removeEventListener("click", this.boundCartActionClick);
      document.removeEventListener("change", this.boundCartActionChange);
      this.boundActionTargets.delete(document);
    }
    this.clearReserveTimer();
  }

  getFocusableElements() {
    const focusableElementsSelector =
      "button, [href], input, select, [tabindex]";
    const focusableElements = () =>
      Array.from(this.querySelectorAll(focusableElementsSelector)).filter(
        (el) => !el.hasAttribute("disabled") && el.tabIndex >= 0,
      );

    return {
      focusableElements,
      first: focusableElements()[0],
      last: focusableElements()[focusableElements().length - 1],
    };
  }

  temporaryHideFocusVisible() {
    document.body.classList.add("no-focus-visible");
  }

  onToggle() {
    if (this.hasAttribute("open")) {
      this.removeAttribute("open");
      this.isOpen = false;
      // this.mainTrigger.focus();
      this.temporaryHideFocusVisible();
      setTabindex(this.toggleEelements(), "-1");
    } else {
      this.setAttribute("open", "");
      this.isOpen = true;
      this.closeButton().focus();
      this.temporaryHideFocusVisible();
      setTabindex(this.toggleEelements(), "0");
    }
  }

  toggleDrawerClasses() {
    this.onToggle();
    this.drawer.classList.toggle(this.classDrawerActive);
    this.body.classList.toggle(this.activeOverlayBodyClass);

    // dispatch a custom event on the document
    const eventName = this.isOpen
      ? PUB_SUB_EVENTS.cartDrawerOpen
      : PUB_SUB_EVENTS.cartDrawerClose;

    document.dispatchEvent(
      new CustomEvent(eventName, {
        bubbles: true,
      }),
    );
  }

  init() {
    this.addEventListener("keydown", (e) => {
      const isTabPressed =
        e.key === "Tab" || e.keyCode === 9 || e.code === "Tab";
      const { first, last } = this.getFocusableElements();

      if (e.key === "Escape" || e.keyCode === 27 || e.code === "Escape") {
        if (this.isOpen) {
          this.toggleDrawerClasses();
        }
      }

      if (isTabPressed) {
        if (e.shiftKey && document.activeElement === first) {
          last.focus();
          e.preventDefault();
        } else if (!e.shiftKey && document.activeElement === last) {
          first.focus();
          e.preventDefault();
        }
      }
    });

    this.triggers().forEach((trigger) => {
      trigger.addEventListener("click", (e) => {
        e.preventDefault();
        this.toggleDrawerClasses();
      });
    });

    this.addEventListener("click", (e) => {
      if (e.target.closest(".wt-cart__drawer__close")) {
        e.preventDefault();
        this.toggleDrawerClasses();
      }
    });

    this.addEventListener("click", (e) => {
      const link = e.target.closest("a.button--continue-shopping");
      if (!link || link.hash !== "#bundle-builder") return;
      if (this.isOpen) {
        this.toggleDrawerClasses();
      }
    });
    this.bindCartActionDelegates(this);

    document.addEventListener("cart-drawer:refresh", (e) =>
      this.refreshCartDrawer(e),
    );
  }

  bindCartActionDelegates(target) {
    if (!target || this.boundActionTargets.has(target)) return;
    target.addEventListener("click", this.boundCartActionClick);
    target.addEventListener("change", this.boundCartActionChange);
    this.boundActionTargets.add(target);
  }

  handleCartActionClick(e) {
    const plusButton = e.target.closest("[data-cart-reco-plus]");
    const minusButton = e.target.closest("[data-cart-reco-minus]");
    const controlButton = plusButton || minusButton;
    if (controlButton) {
      e.preventDefault();

      if (this.isRecoUpdating || controlButton.disabled) return;
      controlButton.disabled = true;
      this.runRecommendationUpdate(
        controlButton,
        plusButton ? "plus" : "minus",
      );
      return;
    }

    const navButton = e.target.closest("[data-cart-reco-scroll]");
    if (navButton) {
      e.preventDefault();

      const direction = navButton.dataset.cartRecoScroll === "prev" ? -1 : 1;
      const recoRoot = navButton.closest(".av-cart-reco, .av-main-cart-flavors");
      const track = recoRoot?.querySelector("[data-cart-reco-track]");
      if (!track) return;

      const step = Math.max(Math.floor(track.clientWidth * 0.82), 180);
      track.scrollBy({
        left: direction * step,
        behavior: "smooth",
      });
      return;
    }

    const upgradeButton = e.target.closest("[data-cart-upgrade-autoship]");
    if (upgradeButton) {
      e.preventDefault();
      if (this.isAutoshipUpdating) return;
      this.runAutoshipUpgrade(upgradeButton);
    }
  }

  handleCartActionChange(e) {
    const frequencySelect = e.target.closest("[data-cart-autoship-frequency]");
    if (!frequencySelect) return;

    const frequencyKey = this.normalizeFrequencyKey(frequencySelect.value);
    if (!frequencyKey) return;
    const selectedOption =
      frequencySelect.options[frequencySelect.selectedIndex] || null;
    const groupId =
      String(frequencySelect.dataset.currentPlanGroupId || "").trim() ||
      String(selectedOption?.dataset?.groupId || "").trim();
    if (this.isAutoshipUpdating) return;

    this.runAutoshipFrequencyUpdate(
      frequencySelect,
      frequencyKey,
      groupId,
    );
  }

  renderContents(parsedState, isClosedCart = true) {
    if (!parsedState || !parsedState.sections) return;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      const sectionHtml = parsedState.sections[section.id];
      if (!sectionElement || !sectionHtml) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(
        sectionHtml,
        section.selector,
      );
    });
    this.setupReserveTimer();
    this.hydrateAutoshipFrequencyOptions();
    setTabindex(this.toggleEelements(), this.isOpen ? "0" : "-1");

    if (isClosedCart) {
      setTimeout(() => {
        this.toggleDrawerClasses();
        if (this.isOpen) {
          this.closeButton().focus();
        }
      });
    }
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    const parsed = new DOMParser().parseFromString(html, "text/html");
    const sectionElement = parsed.querySelector(selector);
    return sectionElement ? sectionElement.innerHTML : "";
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "#CartDrawer",
      },
      {
        id: "cart-icon-bubble",
      },
    ];
  }

  refreshCartDrawer(e) {
    const sectionsToRender = this.getSectionsToRender();
    const rootPath = this.getRootPath();
    const normalizedRootPath = rootPath.endsWith("/") ? rootPath : `${rootPath}/`;
    const sectionIds = sectionsToRender.map((section) => section.id).join(",");
    const requestUrl =
      `${normalizedRootPath}?sections=${sectionIds}` +
      `&sections_url=${encodeURIComponent(window.location.pathname)}` +
      `&_=${Date.now()}`;

    return fetch(requestUrl, {
      credentials: "same-origin",
      cache: "no-store",
      headers: {
        Accept: "application/json",
      },
    })
      .then((response) => response.json())
      .then((response) => {
        const parsedState = {
          sections: response,
        };
        this.renderContents(parsedState, false);
      })
      .catch((e) => {
        console.log(e);
      });
  }

  wait(ms) {
    return new Promise((resolve) => {
      window.setTimeout(resolve, ms);
    });
  }

  getRootPath() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
  }

  getJsonCartEndpoint(url, fallback) {
    if (!url) return fallback;
    if (url.includes(".js")) return url;
    return `${url}.js`;
  }

  fetchCartStateNoCache() {
    const cartUrl = this.getJsonCartEndpoint(
      window.routes && window.routes.cart_url,
      `${this.getRootPath()}cart.js`,
    );
    return fetch(`${cartUrl}?_=${Date.now()}`, {
      credentials: "same-origin",
      cache: "no-store",
      headers: {
        Accept: "application/json",
      },
    }).then((response) => {
      if (!response.ok) {
        throw new Error(`Cart fetch failed (${response.status})`);
      }
      return response.json();
    });
  }

  getDecoratedCartItems(cart) {
    return Array.isArray(cart?.items)
      ? cart.items.map((item, index) => ({
          ...item,
          __line: index + 1,
        }))
      : [];
  }

  postJson(url, payload) {
    return fetch(url, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(payload),
    }).then(async (response) => {
      if (!response.ok) {
        let details = "";
        try {
          const json = await response.clone().json();
          details = String(
            json?.description || json?.message || json?.error || "",
          ).trim();
          if (!details) {
            details = JSON.stringify(json);
          }
        } catch (parseError) {
          try {
            details = String(await response.text()).trim();
          } catch (textError) {
            details = "";
          }
        }
        const requestError = new Error(
          `Cart request failed (${response.status}) ${details}`.trim(),
        );
        requestError.status = response.status;
        requestError.details = details;
        requestError.url = url;
        requestError.payload = payload;
        throw requestError;
      }
      return response;
    });
  }

  setAutoshipUpgradeState(isUpgraded, upgradeOrigin = "one_time_upgrade") {
    const updateUrl = this.getJsonCartEndpoint(
      window.routes && window.routes.cart_update_url,
      `${this.getRootPath()}cart/update.js`,
    );
    return this.postJson(updateUrl, {
      attributes: {
        main_cart_autoship_upgraded: isUpgraded ? "1" : "",
        main_cart_autoship_upgrade_origin: isUpgraded
          ? String(upgradeOrigin || "one_time_upgrade")
          : "",
      },
    });
  }

  async incrementRecommendationItem(button) {
    const variantId = Number(button.dataset.variantId);
    if (!variantId) return { didUpdate: false };

    const addItem = {
      id: variantId,
      quantity: 1,
    };
    const productHandle = String(button.dataset.productHandle || "").trim();
    const explicitPurchaseMode = this.resolveExplicitPurchaseModeFromControl(button);
    const strictModeConsent = this.isStrictModeConsentEnabled(
      this.getSubscriptionConfigFromDom(),
    );
    const canAttachSellingPlan =
      explicitPurchaseMode === "sub" || (!strictModeConsent && explicitPurchaseMode !== "otp");

    if (productHandle && canAttachSellingPlan) {
      try {
        const cart = await this.fetchCartStateNoCache();
        const hasAutoshipLine = (cart.items || []).some(
          (item) => item && item.selling_plan_allocation,
        );

        if (hasAutoshipLine) {
          const currentFrequency = this.getCurrentAutoshipIntervalFromCart(cart);
          const autoshipContext = this.getAutoshipContext(cart);
          const autoshipSelect = this.querySelector("[data-cart-autoship-frequency]");
          const selectedFrequencyKey = this.normalizeFrequencyKey(
            autoshipSelect?.dataset?.currentFrequencyKey ||
              autoshipSelect?.value ||
              "",
          );
          const selectedGroupId = String(
            autoshipSelect?.dataset?.currentPlanGroupId || "",
          ).trim();
          const desiredFrequencyKey =
            selectedFrequencyKey ||
            this.normalizeFrequencyKey(currentFrequency.frequencyKey);
          const desiredGroupId =
            selectedGroupId ||
            String(currentFrequency.groupId || "").trim() ||
            autoshipContext.groupId;
          const resolutionSource = "cart-drawer-reco-plus";
          if (!desiredFrequencyKey) {
            this.debugIssue("reco-plus-plan", "missing-desired-frequency", {
              source: resolutionSource,
              variantId,
              handle: productHandle,
              desiredGroupId,
            });
          }
          const sellingPlanId = await this.resolveSellingPlanIdForItem(
            {
              id: variantId,
              variant_id: variantId,
              handle: productHandle,
            },
            {
              desiredFrequencyKey,
              desiredGroupId,
              fallbackAppId: autoshipContext.config?.fallbackAppId || "5859381",
              resolutionSource,
            },
          );

          if (sellingPlanId > 0) {
            addItem.selling_plan = sellingPlanId;
          } else {
            this.debugIssue("reco-plus-plan", "unresolved-selling-plan", {
              source: resolutionSource,
              variantId,
              handle: productHandle,
              desiredFrequencyKey,
              desiredGroupId,
            });
          }
        }
      } catch (error) {
        console.error(error);
      }
    }

    const addUrl = this.getJsonCartEndpoint(
      window.routes && window.routes.cart_add_url,
      `${this.getRootPath()}cart/add.js`,
    );
    await this.postJson(addUrl, {
      items: [addItem],
    });
    const addedSellingPlanId = Number(addItem.selling_plan || 0);
    return {
      didUpdate: true,
      addedVariantId: variantId,
      addedProductHandle: productHandle,
      addedSellingPlanId,
      purchaseMode:
        explicitPurchaseMode ||
        (addedSellingPlanId > 0 ? "sub" : "otp"),
    };
  }

  async decrementRecommendationItem(button) {
    const variantId = Number(button.dataset.variantId);
    const productId = Number(button.dataset.productId);
    let lineKey = button.dataset.lineKey || "";
    let lineQty = Number(button.dataset.lineQty);
    let changedBundleId = "";

    if (!lineKey || !Number.isFinite(lineQty) || lineQty <= 0) {
      const cartResponse = await fetch(`${this.getRootPath()}cart.js`, {
        credentials: "same-origin",
      });
      if (!cartResponse.ok) return { didUpdate: false };
      const cart = await cartResponse.json();
      const matchByVariant = cart.items.find(
        (item) => Number(item.variant_id) === variantId && Number(item.quantity) > 0,
      );
      const matchByProduct = cart.items.find(
        (item) => Number(item.product_id) === productId && Number(item.quantity) > 0,
      );
      const match = matchByVariant || matchByProduct;
      if (!match) return { didUpdate: false };
      lineKey = match.key || "";
      lineQty = Number(match.quantity);
      changedBundleId = String(match?.properties?._bundle_id || "").trim();
    } else {
      const cart = await this.fetchCartStateNoCache().catch(() => null);
      const matchedLine = (cart?.items || []).find(
        (item) => String(item?.key || "") === String(lineKey || ""),
      );
      changedBundleId = String(matchedLine?.properties?._bundle_id || "").trim();
    }

    if (!lineKey || !Number.isFinite(lineQty) || lineQty <= 0) {
      return { didUpdate: false };
    }
    const changeUrl = this.getJsonCartEndpoint(
      window.routes && window.routes.cart_change_url,
      `${this.getRootPath()}cart/change.js`,
    );
    await this.postJson(changeUrl, {
      id: lineKey,
      quantity: Math.max(lineQty - 1, 0),
    });
    return {
      didUpdate: true,
      changedLineKey: lineKey,
      changedBundleId,
    };
  }

  buildOrphanBundleUpdates(
    cart,
    { protectedBundleIds = [], scopeBundleIds = [] } = {},
  ) {
    const updates = {};
    const items = Array.isArray(cart?.items) ? cart.items : [];
    if (!items.length) return updates;
    const config = this.getSubscriptionConfigFromDom();
    const parentHandles = this.getBundleParentHandlesFromConfig(config);
    const protectedIds = new Set(
      (Array.isArray(protectedBundleIds) ? protectedBundleIds : [])
        .map((bundleId) => String(bundleId || "").trim())
        .filter(Boolean),
    );
    const scopedIds = new Set(
      (Array.isArray(scopeBundleIds) ? scopeBundleIds : [])
        .map((bundleId) => String(bundleId || "").trim())
        .filter(Boolean),
    );

    const bundleGroups = new Map();

    items.forEach((item) => {
      const lineKey = String(item?.key || "");
      if (!lineKey) return;

      const properties = item?.properties || {};
      const bundleId = String(properties?._bundle_id || "").trim();
      const isBundleParent = this.isBundleParentItem(item, parentHandles);
      const quantity = Number(item?.quantity || 0);
      const safeQty = Number.isFinite(quantity) ? quantity : 0;

      if (isBundleParent && !bundleId && safeQty > 0) {
        updates[lineKey] = 0;
        return;
      }

      if (!bundleId) return;

      if (!bundleGroups.has(bundleId)) {
        bundleGroups.set(bundleId, {
          parents: [],
          children: [],
        });
      }

      const group = bundleGroups.get(bundleId);
      const entry = { key: lineKey, quantity: safeQty };
      if (isBundleParent) {
        group.parents.push(entry);
      } else {
        group.children.push(entry);
      }
    });

    bundleGroups.forEach((group, bundleId) => {
      if (protectedIds.has(String(bundleId || "").trim())) return;
      if (scopedIds.size > 0 && !scopedIds.has(String(bundleId || "").trim())) return;
      const parentQty = group.parents.reduce(
        (acc, item) => acc + (Number(item.quantity) || 0),
        0,
      );
      const childQty = group.children.reduce(
        (acc, item) => acc + (Number(item.quantity) || 0),
        0,
      );

      if (parentQty > 0 && childQty > 0) return;

      [...group.parents, ...group.children].forEach((item) => {
        if (!item.key) return;
        updates[item.key] = 0;
      });
    });

    return updates;
  }

  async cleanupOrphanBundleGroups(options = {}) {
    const cart = await this.fetchCartStateNoCache();
    const updates = this.buildOrphanBundleUpdates(cart, options);
    if (Object.keys(updates).length === 0) return false;

    const updateUrl = this.getJsonCartEndpoint(
      window.routes && window.routes.cart_update_url,
      `${this.getRootPath()}cart/update.js`,
    );
    await this.postJson(updateUrl, { updates });
    return true;
  }

  extractProductHandleFromUrl(url) {
    if (!url) return "";
    const cleanUrl = String(url).split("?")[0];
    const match = cleanUrl.match(/\/products\/([^/]+)/i);
    if (!match || !match[1]) return "";
    return decodeURIComponent(match[1]);
  }

  fetchProductJsonByHandle(handle) {
    if (!handle) {
      return Promise.resolve(null);
    }

    const normalizedHandle = String(handle).trim();
    if (!normalizedHandle) {
      return Promise.resolve(null);
    }

    if (this.productJsonCache.has(normalizedHandle)) {
      return this.productJsonCache.get(normalizedHandle);
    }

    const rootPath = this.getRootPath();
    const normalizedRootPath = rootPath.endsWith("/") ? rootPath : `${rootPath}/`;
    const request = fetch(`${normalizedRootPath}products/${normalizedHandle}.js`, {
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(
            `Product request failed (${response.status}) for handle: ${normalizedHandle}`,
          );
        }
        return response.json();
      })
      .catch((error) => {
        this.productJsonCache.delete(normalizedHandle);
        throw error;
      });

    this.productJsonCache.set(normalizedHandle, request);
    return request;
  }

  getFirstSellingPlanIdForVariant(product, variantId) {
    if (!product || !Array.isArray(product.variants)) return 0;

    const targetVariant =
      product.variants.find((variant) => Number(variant.id) === Number(variantId)) ||
      product.variants[0];
    if (!targetVariant) return 0;

    const allocationIds = (Array.isArray(targetVariant.selling_plan_allocations)
      ? targetVariant.selling_plan_allocations
      : []
    )
      .map((allocation) =>
        Number(allocation?.selling_plan_id || allocation?.selling_plan?.id || 0),
      )
      .filter((id) => Number.isFinite(id) && id > 0);

    if (allocationIds.length > 0) {
      return allocationIds[0];
    }

    if (!Array.isArray(product.selling_plan_groups)) return 0;
    for (const group of product.selling_plan_groups) {
      if (!group || !Array.isArray(group.selling_plans)) continue;
      for (const plan of group.selling_plans) {
        const planId = Number(plan?.id || 0);
        if (planId > 0) return planId;
      }
    }

    return 0;
  }

  getVariantSellingPlanIds(product, variantId) {
    if (!product || !Array.isArray(product.variants)) return [];

    const targetVariant =
      product.variants.find((variant) => Number(variant.id) === Number(variantId)) ||
      product.variants[0];
    if (!targetVariant) return [];

    const ids = (Array.isArray(targetVariant.selling_plan_allocations)
      ? targetVariant.selling_plan_allocations
      : []
    )
      .map((allocation) =>
        Number(allocation?.selling_plan_id || allocation?.selling_plan?.id || 0),
      )
      .filter((id) => Number.isFinite(id) && id > 0);

    return Array.from(new Set(ids));
  }

  normalizeIntervalUnit(intervalUnit, fallbackUnit = "") {
    const unit = String(intervalUnit || "").trim().toLowerCase();
    if (!unit) return fallbackUnit;
    return unit.endsWith("s") ? unit.slice(0, -1) : unit;
  }

  areIntervalValuesEquivalent(firstCount, firstUnit, secondCount, secondUnit) {
    const leftCount = Number(firstCount || 0);
    const rightCount = Number(secondCount || 0);
    if (!Number.isFinite(leftCount) || !Number.isFinite(rightCount)) return false;
    if (leftCount <= 0 || rightCount <= 0) return false;

    const leftUnit = this.normalizeIntervalUnit(firstUnit || "", "");
    const rightUnit = this.normalizeIntervalUnit(secondUnit || "", "");
    if (!leftUnit || !rightUnit) return false;
    if (leftUnit === rightUnit) return leftCount === rightCount;

    if (leftUnit === "month" && rightUnit === "week") {
      return rightCount === leftCount * 4;
    }
    if (leftUnit === "week" && rightUnit === "month") {
      return leftCount === rightCount * 4;
    }

    return false;
  }

  formatIntervalOptionLabel(intervalCount, intervalUnit, fallbackLabel = "") {
    const normalizedFallbackLabel = String(fallbackLabel || "").trim();
    if (normalizedFallbackLabel) return normalizedFallbackLabel;
    const count = Number(intervalCount);
    const safeCount = Number.isFinite(count) && count > 0 ? count : 1;
    const singularUnit = this.normalizeIntervalUnit(intervalUnit, "month");
    const unitLabel = safeCount === 1 ? singularUnit : `${singularUnit}s`;
    return `${safeCount} ${unitLabel}`;
  }

  getCurrentAutoshipIntervalFromCart(cart) {
    const autoshipLines = (cart?.items || []).filter(
      (item) => Number(item?.quantity || 0) > 0 && item?.selling_plan_allocation,
    );
    const autoshipLine = autoshipLines[0];
    if (!autoshipLine) {
      return {
        count: 0,
        unit: "month",
        frequencyKey: "",
        frequencyLabel: "",
        groupId: "",
        planId: 0,
      };
    }

    const sellingPlan = autoshipLine.selling_plan_allocation?.selling_plan;
    const intervalData = this.getPlanIntervalData(sellingPlan, "month");
    const fallbackInterval = {
      count: Number(intervalData.count || 0),
      unit: this.normalizeIntervalUnit(intervalData.unit || "month", "month"),
      frequencyKey: this.getFrequencyKeyFromInterval(intervalData, sellingPlan?.id),
      frequencyLabel: this.getPlanFrequencyLabel(sellingPlan),
      groupId: this.getLineGroupId(autoshipLine),
      planId: Number(sellingPlan?.id || 0),
    };

    const groupedCandidates = [];
    autoshipLines.forEach((lineItem, index) => {
      const lineFrequency = this.getLineFrequencyData(lineItem);
      const quantityWeight = Math.max(1, Number(lineItem?.quantity || 0));
      const candidateKey = this.normalizeFrequencyKey(
        lineFrequency.frequencyKey ||
          this.getFrequencyKeyFromInterval(
            {
              count: Number(lineFrequency.count || 0),
              unit: this.normalizeIntervalUnit(lineFrequency.unit || "month", "month"),
            },
            Number(lineItem?.selling_plan_allocation?.selling_plan?.id || 0),
          ),
      );
      const existingGroup = groupedCandidates.find(
        (groupedCandidate) =>
          this.isFrequencyKeyMatch(groupedCandidate.frequencyKey, candidateKey) ||
          (
            Number(groupedCandidate.count || 0) === Number(lineFrequency.count || 0) &&
            this.normalizeIntervalUnit(groupedCandidate.unit || "", "") ===
              this.normalizeIntervalUnit(lineFrequency.unit || "", "")
          ),
      );

      if (!existingGroup) {
        groupedCandidates.push({
          count: Number(lineFrequency.count || 0),
          unit: this.normalizeIntervalUnit(lineFrequency.unit || "month", "month"),
          frequencyKey: candidateKey,
          frequencyLabel: String(lineFrequency.frequencyLabel || "").trim(),
          groupId: String(lineFrequency.groupId || "").trim(),
          planId: Number(lineItem?.selling_plan_allocation?.selling_plan?.id || 0),
          hits: quantityWeight,
          firstIndex: index,
        });
        return;
      }

      existingGroup.hits += quantityWeight;
      if (!existingGroup.groupId && lineFrequency.groupId) {
        existingGroup.groupId = String(lineFrequency.groupId || "").trim();
      }
      if (!existingGroup.frequencyLabel && lineFrequency.frequencyLabel) {
        existingGroup.frequencyLabel = String(lineFrequency.frequencyLabel || "").trim();
      }
      if (
        Number(existingGroup.planId || 0) <= 0 &&
        Number(lineItem?.selling_plan_allocation?.selling_plan?.id || 0) > 0
      ) {
        existingGroup.planId = Number(
          lineItem?.selling_plan_allocation?.selling_plan?.id || 0,
        );
      }
    });

    const selectedCandidate = groupedCandidates.sort((firstOption, secondOption) => {
      if (secondOption.hits !== firstOption.hits) {
        return secondOption.hits - firstOption.hits;
      }
      return firstOption.firstIndex - secondOption.firstIndex;
    })[0];

    if (!selectedCandidate) {
      return fallbackInterval;
    }

    return {
      count: Number(selectedCandidate.count || fallbackInterval.count || 0),
      unit: this.normalizeIntervalUnit(
        selectedCandidate.unit || fallbackInterval.unit || "month",
        "month",
      ),
      frequencyKey: this.normalizeFrequencyKey(
        selectedCandidate.frequencyKey || fallbackInterval.frequencyKey,
      ),
      frequencyLabel:
        String(selectedCandidate.frequencyLabel || "").trim() ||
        String(fallbackInterval.frequencyLabel || "").trim(),
      groupId:
        String(selectedCandidate.groupId || "").trim() ||
        String(fallbackInterval.groupId || "").trim(),
      planId: Number(selectedCandidate.planId || fallbackInterval.planId || 0),
    };
  }

  getAutoshipIntervalOptionsForVariant(product, variantId) {
    const optionsMap = new Map();
    if (!product || !Array.isArray(product.variants)) return [];

    const variantPlanIds = new Set(this.getVariantSellingPlanIds(product, variantId));
    if (!Array.isArray(product.selling_plan_groups)) return [];

    for (const group of product.selling_plan_groups) {
      if (!group || !Array.isArray(group.selling_plans)) continue;
      const groupId = String(
        group?.id || group?.selling_plan_group_id || group?.app_id || "",
      ).trim();
      for (const plan of group.selling_plans) {
        const planId = Number(plan?.id || 0);
        if (planId <= 0) continue;
        if (variantPlanIds.size > 0 && !variantPlanIds.has(planId)) continue;

        const intervalData = this.getPlanIntervalData(plan, "month");
        const parsedFrequency = this.parseFrequencyKey(
          this.getFrequencyKeyFromInterval(intervalData, planId),
        );
        const count =
          Number(intervalData.count || 0) > 0
            ? Number(intervalData.count || 0)
            : Number(parsedFrequency.count || 0);
        const unit = this.normalizeIntervalUnit(
          intervalData.unit || parsedFrequency.unit || "month",
          "month",
        );
        const frequencyKey = this.normalizeFrequencyKey(
          this.getFrequencyKeyFromInterval({ count, unit }, planId),
        );
        if (!frequencyKey && (!Number.isFinite(count) || count <= 0)) continue;

        const key = `${groupId}:${frequencyKey}`;
        const nextOption = {
          key: frequencyKey,
          count,
          unit,
          planId,
          groupId,
          frequencyKey,
          frequencyLabel: this.getPlanFrequencyLabel(plan),
          name: String(plan?.name || "").trim(),
        };
        const existingOption = optionsMap.get(key);
        if (!existingOption || Number(planId) < Number(existingOption.planId || 0)) {
          optionsMap.set(key, nextOption);
        }
      }
    }

    return Array.from(optionsMap.values()).sort((a, b) => {
      const firstWeight = this.getFrequencySortWeight(a?.count, a?.unit);
      const secondWeight = this.getFrequencySortWeight(b?.count, b?.unit);
      if (firstWeight !== secondWeight) return firstWeight - secondWeight;
      return Number(a?.planId || 0) - Number(b?.planId || 0);
    });
  }

  getSellingPlanMapFromDom() {
    const mapElement = this.querySelector("[data-cart-drawer-selling-plan-map]");
    const rawText = mapElement?.textContent?.trim();
    if (!rawText) return new Map();

    try {
      const parsed = JSON.parse(rawText);
      const planMap = new Map();

      Object.entries(parsed || {}).forEach(([variantKey, optionList]) => {
        const variantId = Number(variantKey || 0);
        if (!Number.isFinite(variantId) || variantId <= 0) return;
        if (!Array.isArray(optionList)) {
          planMap.set(variantId, []);
          return;
        }

        const normalizedOptions = [];
        const seenOptions = new Set();
        optionList.forEach((optionData) => {
          const count = Number(optionData?.count || 0);
          const unit = this.normalizeIntervalUnit(optionData?.unit || "", "");
          const planId = Number(optionData?.planId || optionData?.id || 0);
          const groupId = String(optionData?.groupId || "").trim();
          const fallbackFrequencyKey = this.getFrequencyKeyFromInterval(
            { count, unit },
            planId,
          );
          const frequencyKey = this.normalizeFrequencyKey(
            optionData?.frequencyKey || fallbackFrequencyKey,
          );
          const parsedFrequency = this.parseFrequencyKey(frequencyKey);
          const resolvedCount =
            Number.isFinite(count) && count > 0
              ? count
              : Number(parsedFrequency.count || 0);
          const resolvedUnit = unit || parsedFrequency.unit || "";
          if (!Number.isFinite(planId) || planId <= 0) return;
          if (
            !frequencyKey &&
            (!Number.isFinite(resolvedCount) || resolvedCount <= 0)
          ) {
            return;
          }
          const dedupeKey = `${groupId}:${frequencyKey}:${planId}`;
          if (seenOptions.has(dedupeKey)) return;
          seenOptions.add(dedupeKey);
          normalizedOptions.push({
            key:
              frequencyKey ||
              this.getFrequencyKeyFromInterval(
                { count: resolvedCount, unit: resolvedUnit },
                planId,
              ),
            count: resolvedCount,
            unit: resolvedUnit,
            planId,
            groupId,
            frequencyKey:
              frequencyKey ||
              this.getFrequencyKeyFromInterval(
                { count: resolvedCount, unit: resolvedUnit },
                planId,
              ),
            frequencyLabel: String(
              optionData?.frequencyLabel || optionData?.name || "",
            ).trim(),
            name: String(optionData?.name || "").trim(),
            source: "dom",
          });
        });

        normalizedOptions.sort((a, b) => {
          const firstWeight = this.getFrequencySortWeight(a?.count, a?.unit);
          const secondWeight = this.getFrequencySortWeight(b?.count, b?.unit);
          if (firstWeight !== secondWeight) return firstWeight - secondWeight;
          return Number(a?.planId || 0) - Number(b?.planId || 0);
        });

        planMap.set(variantId, normalizedOptions);
      });

      return planMap;
    } catch (error) {
      this.debugWarn("Failed to parse drawer selling plan map", {
        error: error?.message || String(error),
        rawText,
      });
      console.error(error);
      return new Map();
    }
  }

  getAutoshipIntervalOptionsFromDom(variantId) {
    const safeVariantId = Number(variantId || 0);
    if (!Number.isFinite(safeVariantId) || safeVariantId <= 0) return [];
    const planMap = this.getSellingPlanMapFromDom();
    return Array.isArray(planMap.get(safeVariantId)) ? planMap.get(safeVariantId) : [];
  }

  mergeAutoshipIntervalOptions(...optionGroups) {
    const mergedMap = new Map();
    optionGroups.forEach((optionGroup) => {
      if (!Array.isArray(optionGroup)) return;
      optionGroup.forEach((optionData) => {
        const count = Number(optionData?.count || 0);
        const unit = this.normalizeIntervalUnit(optionData?.unit || "", "");
        const planId = Number(optionData?.planId || 0);
        const groupId = String(optionData?.groupId || "").trim();
        const providedFrequencyKey = this.normalizeFrequencyKey(
          optionData?.frequencyKey,
        );
        const fallbackFrequencyKey = this.getFrequencyKeyFromInterval(
          { count, unit },
          planId,
        );
        const frequencyKey = providedFrequencyKey || fallbackFrequencyKey;
        const parsedFrequency = this.parseFrequencyKey(frequencyKey);
        const resolvedCount =
          Number.isFinite(count) && count > 0
            ? count
            : Number(parsedFrequency.count || 0);
        const resolvedUnit = unit || parsedFrequency.unit || "";
        if (!Number.isFinite(planId) || planId <= 0) return;
        if (
          !frequencyKey &&
          (!Number.isFinite(resolvedCount) || resolvedCount <= 0)
        ) {
          return;
        }
        const key = `${groupId}:${frequencyKey}`;
        const existingOption = mergedMap.get(key);
        if (!existingOption || Number(planId) < Number(existingOption.planId || 0)) {
          mergedMap.set(key, {
            key: frequencyKey,
            count: resolvedCount,
            unit: resolvedUnit,
            planId,
            groupId,
            frequencyKey,
            frequencyLabel: String(
              optionData?.frequencyLabel || optionData?.name || "",
            ).trim(),
            name: String(optionData?.name || "").trim(),
            source: optionData?.source || "unknown",
          });
        }
      });
    });

    return Array.from(mergedMap.values()).sort((a, b) => {
      const firstWeight = this.getFrequencySortWeight(a?.count, a?.unit);
      const secondWeight = this.getFrequencySortWeight(b?.count, b?.unit);
      if (firstWeight !== secondWeight) return firstWeight - secondWeight;
      return Number(a?.planId || 0) - Number(b?.planId || 0);
    });
  }

  async getAutoshipFrequencyOptions(cart) {
    const autoshipItems = (cart?.items || []).filter(
      (item) => Number(item?.quantity || 0) > 0 && item?.selling_plan_allocation,
    );
    if (autoshipItems.length === 0) {
      return {
        options: [],
        candidates: [],
        groupId: "",
        fallbackAppId: "5859381",
      };
    }

    const context = this.getAutoshipContext(cart);
    const firstAutoshipGroupId = String(
      this.getLineGroupId(autoshipItems[0]) || "",
    ).trim();
    const targetGroupId = String(context.groupId || "").trim() || firstAutoshipGroupId;
    const fallbackAppId = String(context?.config?.fallbackAppId || "5859381").trim();
    const optionsMap = new Map();
    const frequencyCandidates = [];
    for (const item of autoshipItems) {
      frequencyCandidates.push(this.getLineFrequencyData(item));
      const variantId = Number(item?.variant_id || item?.id || 0);
      if (!Number.isFinite(variantId) || variantId <= 0) continue;

      const productHandle =
        item?.handle ||
        item?.product_handle ||
        this.extractProductHandleFromUrl(item?.url || "");

      const domOptions = this.getAutoshipIntervalOptionsFromDom(variantId);
      let mergedOptions = [];
      if (domOptions.length > 0) {
        mergedOptions = this.mergeAutoshipIntervalOptions(domOptions);
      } else {
        let productOptions = [];
        if (productHandle) {
          try {
            const product = await this.fetchProductJsonByHandle(productHandle);
            productOptions = this.getAutoshipIntervalOptionsForVariant(
              product,
              variantId,
            );
          } catch (error) {
            console.error(error);
          }
        }
        mergedOptions = this.mergeAutoshipIntervalOptions(
          productOptions,
          domOptions,
        );
      }
      const scopedOptions = this.getGroupMatchedOptions(
        mergedOptions,
        targetGroupId,
        fallbackAppId,
      );
      scopedOptions.forEach((optionData) => {
        const normalizedKey = this.normalizeFrequencyKey(
          optionData?.frequencyKey || optionData?.key,
        );
        if (!normalizedKey) return;
        const normalizedGroupId = String(targetGroupId || optionData?.groupId || "")
          .trim();
        const optionMapKey = `${normalizedGroupId}:${normalizedKey}`;
        const existingOption = optionsMap.get(optionMapKey);
        const nextOption = {
          ...optionData,
          key: normalizedKey,
          frequencyKey: normalizedKey,
          groupId: normalizedGroupId,
        };
        if (
          !existingOption ||
          Number(nextOption.planId || 0) < Number(existingOption.planId || 0)
        ) {
          optionsMap.set(optionMapKey, nextOption);
        }
      });
    }

    const options = Array.from(optionsMap.values()).sort((a, b) => {
      const firstWeight = this.getFrequencySortWeight(a?.count, a?.unit);
      const secondWeight = this.getFrequencySortWeight(b?.count, b?.unit);
      if (firstWeight !== secondWeight) return firstWeight - secondWeight;
      return Number(a?.planId || 0) - Number(b?.planId || 0);
    });
    this.debugLog("Resolved drawer autoship frequency options", {
      groupId: targetGroupId,
      fallbackAppId,
      options: options.map((optionData) => ({
        frequencyKey: optionData?.frequencyKey || optionData?.key,
        planId: Number(optionData?.planId || 0),
        groupId: String(optionData?.groupId || ""),
        label: String(optionData?.frequencyLabel || optionData?.name || ""),
      })),
      candidates: frequencyCandidates,
    });
    return {
      options,
      candidates: frequencyCandidates,
      groupId: targetGroupId,
      fallbackAppId,
    };
  }

  hydrateAutoshipFrequencyOptions() {
    const select = this.querySelector("[data-cart-autoship-frequency]");
    if (!select) return Promise.resolve();

    const requestId = (this.autoshipOptionsRequestId += 1);
    if (!this.isAutoshipUpdating) {
      select.disabled = true;
    }

    const fallbackFrequencyKey = this.normalizeFrequencyKey(
      select.dataset.currentFrequencyKey || select.value || "",
    );
    const fallbackGroupId = String(select.dataset.currentPlanGroupId || "").trim();
    const fallbackPlanId = Number(select.dataset.currentPlanId || 0);

    return this.fetchCartStateNoCache()
      .then(async (cart) => {
        const optionsResult = await this.getAutoshipFrequencyOptions(cart);
        const normalizedOptions = Array.isArray(optionsResult?.options)
          ? optionsResult.options
          : [];
        const fallbackAppId = String(optionsResult?.fallbackAppId || "5859381").trim();
        const targetGroupId =
          String(optionsResult?.groupId || "").trim() ||
          String(select.dataset.currentPlanGroupId || "").trim();
        if (requestId !== this.autoshipOptionsRequestId || !select.isConnected) return;

        const currentFromCart = this.getCurrentAutoshipIntervalFromCart(cart);
        const pendingFrequencySelection = this.getRecentFrequencySelection();
        const selectedFrequencyKey =
          this.normalizeFrequencyKey(pendingFrequencySelection?.frequencyKey || "") ||
          this.normalizeFrequencyKey(currentFromCart.frequencyKey) ||
          fallbackFrequencyKey;
        const selectedGroupId =
          String(pendingFrequencySelection?.groupId || "").trim() ||
          targetGroupId ||
          String(currentFromCart.groupId || "").trim() ||
          fallbackGroupId;
        const selectedPlanId = Number(currentFromCart.planId || fallbackPlanId || 0);
        const scopedNormalizedOptions = this.getGroupMatchedOptions(
          normalizedOptions,
          selectedGroupId || targetGroupId,
          fallbackAppId,
        );
        const optionsForSelect =
          scopedNormalizedOptions.length > 0
            ? scopedNormalizedOptions
            : normalizedOptions;

        if (optionsForSelect.length === 0 && selectedFrequencyKey) {
          const parsedSelectedFrequency = this.parseFrequencyKey(selectedFrequencyKey);
          optionsForSelect.push({
            key: selectedFrequencyKey,
            frequencyKey: selectedFrequencyKey,
            count: Number(parsedSelectedFrequency.count || 0),
            unit: this.normalizeIntervalUnit(
              parsedSelectedFrequency.unit || select.dataset.currentUnit || "month",
              "month",
            ),
            planId: fallbackPlanId,
            groupId: selectedGroupId,
            frequencyLabel:
              String(
                select.options[select.selectedIndex]?.textContent ||
                  currentFromCart.frequencyLabel ||
                  "",
              ).trim() || "Monthly",
            isFallback: true,
          });
        }

        if (optionsForSelect.length === 0) return;

        select.innerHTML = "";
        optionsForSelect.forEach((optionData) => {
          const option = document.createElement("option");
          const normalizedFrequencyKey = this.normalizeFrequencyKey(
            optionData?.frequencyKey || optionData?.key,
          );
          option.value = String(normalizedFrequencyKey);
          option.dataset.frequencyKey = String(normalizedFrequencyKey);
          option.dataset.planId = String(Number(optionData?.planId || 0));
          option.dataset.groupId = String(selectedGroupId || optionData?.groupId || "");
          option.dataset.intervalCount = String(Number(optionData?.count || 0));
          option.dataset.intervalUnit = this.normalizeIntervalUnit(
            optionData?.unit || "month",
            "month",
          );
          option.textContent = this.formatIntervalOptionLabel(
            optionData.count,
            optionData.unit,
            optionData.frequencyLabel || optionData.name,
          );
          select.appendChild(option);
        });

        const matchedOption =
          (selectedPlanId > 0
            ? optionsForSelect.find(
                (optionData) => Number(optionData?.planId || 0) === selectedPlanId,
              )
            : null) ||
          this.findFrequencyOption(
            optionsForSelect,
            selectedFrequencyKey,
            selectedGroupId,
            fallbackAppId,
          ) ||
          optionsForSelect[0];

        const matchedFrequencyKey = this.normalizeFrequencyKey(
          matchedOption?.frequencyKey || matchedOption?.key,
        );
        select.value = matchedFrequencyKey;
        select.dataset.currentFrequencyKey = matchedFrequencyKey;
        select.dataset.currentPlanId = String(Number(matchedOption?.planId || 0));
        select.dataset.currentPlanGroupId = String(
          selectedGroupId || matchedOption?.groupId || "",
        );
        select.dataset.currentInterval = String(Number(matchedOption?.count || 0));
        select.dataset.currentUnit = this.normalizeIntervalUnit(
          matchedOption?.unit || "month",
          "month",
        );
        if (
          pendingFrequencySelection &&
          this.isFrequencyKeyMatch(
            matchedFrequencyKey,
            pendingFrequencySelection.frequencyKey,
          ) &&
          (
            !pendingFrequencySelection.groupId ||
            this.isGroupIdMatch(
              String(select.dataset.currentPlanGroupId || ""),
              pendingFrequencySelection.groupId,
            )
          )
        ) {
          this.clearFrequencySelection();
        }
        this.debugLog("Hydrated drawer autoship select", {
          selectedFrequencyKey: matchedFrequencyKey,
          selectedGroupId: String(select.dataset.currentPlanGroupId || ""),
          selectedPlanId: Number(select.dataset.currentPlanId || 0),
          optionCount: optionsForSelect.length,
          options: optionsForSelect.map((optionData) => ({
            frequencyKey: optionData?.frequencyKey || optionData?.key,
            planId: Number(optionData?.planId || 0),
            groupId: String(optionData?.groupId || ""),
            label: String(optionData?.frequencyLabel || optionData?.name || ""),
          })),
        });
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        if (
          requestId === this.autoshipOptionsRequestId &&
          select.isConnected &&
          !this.isAutoshipUpdating
        ) {
          select.disabled = false;
        }
      });
  }

  getSellingPlanIdForVariantAndInterval(
    product,
    variantId,
    intervalCount,
    fallbackPlanId = 0,
    intervalUnit = "",
  ) {
    const desiredInterval = Number(intervalCount);
    const desiredUnit = this.normalizeIntervalUnit(intervalUnit || "", "");
    if (!Number.isFinite(desiredInterval) || desiredInterval <= 0) {
      return this.getFirstSellingPlanIdForVariant(product, variantId);
    }

    const variantPlanIds = this.getVariantSellingPlanIds(product, variantId);
    if (Array.isArray(product?.selling_plan_groups)) {
      for (const group of product.selling_plan_groups) {
        if (!group || !Array.isArray(group.selling_plans)) continue;
        for (const plan of group.selling_plans) {
          const planId = Number(plan?.id || 0);
          if (planId <= 0) continue;
          if (variantPlanIds.length > 0 && !variantPlanIds.includes(planId)) continue;

          const intervalData = this.getPlanIntervalData(
            plan,
            desiredUnit || "month",
          );
          const count = Number(intervalData.count || 0);
          const unit = this.normalizeIntervalUnit(
            intervalData.unit || "month",
            "month",
          );
          if (!desiredUnit && count === desiredInterval) {
            return planId;
          }
          if (
            desiredUnit &&
            this.areIntervalValuesEquivalent(
              desiredInterval,
              desiredUnit,
              count,
              unit,
            )
          ) {
            return planId;
          }
        }
      }
    }

    const normalizedFallbackId = Number(fallbackPlanId || 0);
    if (
      normalizedFallbackId > 0 &&
      (variantPlanIds.length === 0 || variantPlanIds.includes(normalizedFallbackId))
    ) {
      return normalizedFallbackId;
    }

    if (variantPlanIds.length > 0) {
      return variantPlanIds[0];
    }

    return this.getFirstSellingPlanIdForVariant(product, variantId);
  }

  getCleanLineItemProperties(properties) {
    if (!properties || typeof properties !== "object") return undefined;

    const sanitizedProperties = {};
    Object.entries(properties).forEach(([key, value]) => {
      if (value === null || value === undefined) return;
      const normalizedValue = String(value).trim();
      if (!normalizedValue) return;
      sanitizedProperties[key] = normalizedValue;
    });

    return Object.keys(sanitizedProperties).length > 0
      ? sanitizedProperties
      : undefined;
  }

  async resolveSellingPlanIdForItem(
    item,
    {
      desiredFrequencyKey = "",
      desiredGroupId = "",
      fallbackPlanId = 0,
      fallbackPlanName = "",
      fallbackAppId = "",
      resolutionSource = "",
    } = {},
  ) {
    const variantId = Number(item?.variant_id || item?.id || item?.variantId || 0);
    if (variantId <= 0) return 0;

    const productHandle =
      item?.handle ||
      item?.product_handle ||
      this.extractProductHandleFromUrl(item?.url || "");
    const normalizedResolutionSource = String(resolutionSource || "").trim();
    if (!productHandle) {
      if (normalizedResolutionSource) {
        this.debugIssue("reco-plus-plan", "missing-product-handle", {
          source: normalizedResolutionSource,
          variantId,
          desiredFrequencyKey: this.normalizeFrequencyKey(desiredFrequencyKey),
          desiredGroupId: String(desiredGroupId || "").trim(),
        });
      }
      return Number(fallbackPlanId || 0);
    }

    const normalizedDesiredFrequencyKey = this.normalizeFrequencyKey(
      desiredFrequencyKey,
    );
    const normalizedDesiredGroupId = String(desiredGroupId || "").trim();
    const normalizedFallbackAppId = String(
      fallbackAppId || this.getSubscriptionConfigFromDom().fallbackAppId || "",
    ).trim();
    const parsedDesiredFrequency = this.parseFrequencyKey(
      normalizedDesiredFrequencyKey,
    );
    const desiredInterval = Number(parsedDesiredFrequency.count || 0);
    const desiredUnit = this.normalizeIntervalUnit(
      parsedDesiredFrequency.unit || "",
      "month",
    );

    try {
      const product = await this.fetchProductJsonByHandle(productHandle);
      const currentPlanId =
        Number(item?.selling_plan_allocation?.selling_plan?.id || 0) ||
        Number(fallbackPlanId || 0);

      const productIntervalOptions = this.getAutoshipIntervalOptionsForVariant(
        product,
        variantId,
      );
      const domIntervalOptions = this.getAutoshipIntervalOptionsFromDom(variantId);
      const intervalOptions =
        domIntervalOptions.length > 0
          ? this.mergeAutoshipIntervalOptions(domIntervalOptions)
          : this.mergeAutoshipIntervalOptions(
              productIntervalOptions,
              domIntervalOptions,
            );
      const summarizeOptions = (optionList) =>
        (Array.isArray(optionList) ? optionList : []).slice(0, 4).map((optionData) => ({
          frequencyKey: this.getOptionFrequencyKey(optionData),
          groupId: String(optionData?.groupId || "").trim(),
          planId: Number(optionData?.planId || 0),
          label: String(optionData?.frequencyLabel || optionData?.name || "").trim(),
        }));
      const desiredOption = this.findFrequencyOption(
        intervalOptions,
        normalizedDesiredFrequencyKey,
        normalizedDesiredGroupId,
        normalizedFallbackAppId,
      );
      if (Number(desiredOption?.planId || 0) > 0) {
        return Number(desiredOption.planId || 0);
      }

      const resolvedPlanId = this.getSellingPlanIdForVariantAndInterval(
        product,
        variantId,
        desiredInterval,
        currentPlanId,
        desiredUnit,
      );
      if (Number(resolvedPlanId || 0) > 0) {
        return Number(resolvedPlanId || 0);
      }

      const scopedOptions = this.getGroupMatchedOptions(
        intervalOptions,
        normalizedDesiredGroupId,
        normalizedFallbackAppId,
      );
      if (Number(fallbackPlanId || 0) > 0) {
        const hasFallbackPlanInScope = scopedOptions.some(
          (optionData) =>
            Number(optionData?.planId || 0) === Number(fallbackPlanId || 0),
        );
        if (hasFallbackPlanInScope) {
          if (normalizedResolutionSource) {
            this.debugIssue("reco-plus-plan", "fallback-fallback-plan", {
              source: normalizedResolutionSource,
              variantId,
              handle: productHandle,
              desiredFrequencyKey: normalizedDesiredFrequencyKey,
              desiredGroupId: normalizedDesiredGroupId,
              chosenPlanId: Number(fallbackPlanId || 0),
              options: summarizeOptions(
                scopedOptions.length > 0 ? scopedOptions : intervalOptions,
              ),
            });
          }
          return Number(fallbackPlanId || 0);
        }
      }

      const variantPlanIds = this.getVariantSellingPlanIds(product, variantId);
      if (variantPlanIds.length > 0) {
        if (normalizedResolutionSource) {
          this.debugIssue("reco-plus-plan", "fallback-first-variant-plan", {
            source: normalizedResolutionSource,
            variantId,
            handle: productHandle,
            desiredFrequencyKey: normalizedDesiredFrequencyKey,
            desiredGroupId: normalizedDesiredGroupId,
            chosenPlanId: Number(variantPlanIds[0] || 0),
            options: summarizeOptions(
              scopedOptions.length > 0 ? scopedOptions : intervalOptions,
            ),
          });
        }
        return Number(variantPlanIds[0] || 0);
      }
    } catch (error) {
      if (normalizedResolutionSource) {
        this.debugIssue("reco-plus-plan", "resolve-error", {
          source: normalizedResolutionSource,
          variantId,
          handle: productHandle,
          desiredFrequencyKey: normalizedDesiredFrequencyKey,
          desiredGroupId: normalizedDesiredGroupId,
          error: error?.message || String(error),
        });
      }
      console.error(error);
    }

    if (normalizedResolutionSource) {
      this.debugIssue("reco-plus-plan", "fallback-final-plan", {
        source: normalizedResolutionSource,
        variantId,
        handle: productHandle,
        desiredFrequencyKey: normalizedDesiredFrequencyKey,
        desiredGroupId: normalizedDesiredGroupId,
        chosenPlanId: Number(fallbackPlanId || 0),
      });
    }
    return Number(fallbackPlanId || 0);
  }

  async convertLineToSellingPlan(lineItem, sellingPlanId) {
    const lineKey = lineItem?.key || "";
    const lineNumber = Number(lineItem?.line || lineItem?.__line || 0);
    const quantity = Number(lineItem?.quantity || 0);
    const variantId = Number(lineItem?.variant_id || lineItem?.id || 0);
    const resolvedPlanId = Number(sellingPlanId || 0);
    if (!lineKey || quantity <= 0 || variantId <= 0 || resolvedPlanId <= 0) {
      return false;
    }
    const upgradedProperties = {
      ...(this.getCleanLineItemProperties(lineItem.properties) || {}),
      _main_cart_autoship_upgraded: "1",
    };

    const changeUrl = this.getJsonCartEndpoint(
      window.routes && window.routes.cart_change_url,
      `${this.getRootPath()}cart/change.js`,
    );
    const addUrl = this.getJsonCartEndpoint(
      window.routes && window.routes.cart_add_url,
      `${this.getRootPath()}cart/add.js`,
    );
    const updateUrl = this.getJsonCartEndpoint(
      window.routes && window.routes.cart_update_url,
      `${this.getRootPath()}cart/update.js`,
    );

    const changePayload =
      lineNumber > 0
        ? {
            line: lineNumber,
            quantity,
            selling_plan: resolvedPlanId,
            properties: upgradedProperties,
          }
        : {
            id: lineKey,
            quantity,
            selling_plan: resolvedPlanId,
            properties: upgradedProperties,
          };

    try {
      await this.postJson(changeUrl, changePayload);
      return true;
    } catch (error) {
      const isBadRequest = Number(error?.status || 0) === 400;
      if (!isBadRequest) {
        console.error(error);
      } else if (lineKey) {
        try {
          await this.postJson(changeUrl, {
            id: lineKey,
            quantity,
            selling_plan: resolvedPlanId,
            properties: upgradedProperties,
          });
          return true;
        } catch (retryError) {
          if (Number(retryError?.status || 0) !== 400) {
            console.error(retryError);
          }
        }
      }
    }

    const nextItem = {
      id: variantId,
      quantity,
      selling_plan: resolvedPlanId,
      properties: upgradedProperties,
    };

    try {
      await this.postJson(addUrl, {
        items: [nextItem],
      });
      await this.postJson(updateUrl, {
        updates: {
          [lineKey]: 0,
        },
      });
      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  }

  async waitForAutoshipConversion(convertedLineKeys) {
    const pendingKeys = new Set(
      (Array.isArray(convertedLineKeys) ? convertedLineKeys : []).filter(Boolean),
    );
    const maxAttempts = 8;

    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      const cart = await this.fetchCartStateNoCache();
      const hasPendingOneTimeLine = cart.items.some((item) => {
        if (!item || !item.key) return false;
        return pendingKeys.has(item.key) && !item.selling_plan_allocation;
      });
      const hasAutoshipLine = cart.items.some(
        (item) => item && item.selling_plan_allocation,
      );

      if (!hasPendingOneTimeLine && hasAutoshipLine) {
        return true;
      }

      await this.wait(120);
    }

    return false;
  }

  async normalizeRemainingOneTimeLinesToAutoship(
    desiredFrequencyKey = "",
    desiredGroupId = "",
    fallbackAppId = "",
  ) {
    const cart = await this.fetchCartStateNoCache();
    const cartItems = this.getDecoratedCartItems(cart);
    const oneTimeItems = cartItems.filter(
      (item) =>
        Number(item?.quantity || 0) > 0 &&
        !item?.selling_plan_allocation &&
        !item?.has_components,
    );
    const autoshipItems = cartItems.filter(
      (item) => Number(item?.quantity || 0) > 0 && item?.selling_plan_allocation,
    );
    if (oneTimeItems.length === 0 || autoshipItems.length === 0) return false;
    const normalizedDesiredFrequencyKey = this.normalizeFrequencyKey(
      desiredFrequencyKey,
    );

    const getItemHandle = (item) =>
      String(
        item?.handle ||
          item?.product_handle ||
          this.extractProductHandleFromUrl(item?.url || ""),
      )
        .trim()
        .toLowerCase();

    let didUpdateAny = false;
    for (const oneTimeItem of oneTimeItems) {
      const variantId = Number(oneTimeItem?.variant_id || oneTimeItem?.id || 0);
      const productId = Number(oneTimeItem?.product_id || 0);
      if (variantId <= 0) continue;

      const oneTimeHandle = getItemHandle(oneTimeItem);
      const variantMatch = autoshipItems.find(
        (item) => Number(item?.variant_id || item?.id || 0) === variantId,
      );
      const productMatch =
        autoshipItems.find(
          (item) => Number(item?.product_id || 0) === productId && productId > 0,
        ) ||
        autoshipItems.find((item) => {
          const autoshipHandle = getItemHandle(item);
          return oneTimeHandle && autoshipHandle && autoshipHandle === oneTimeHandle;
        });

      const candidatePlanId = Number(
        variantMatch?.selling_plan_allocation?.selling_plan?.id ||
          productMatch?.selling_plan_allocation?.selling_plan?.id ||
          0,
      );
      const candidatePlanName =
        variantMatch?.selling_plan_allocation?.selling_plan?.name ||
        productMatch?.selling_plan_allocation?.selling_plan?.name ||
        "";
      const candidateFrequency = this.getLineFrequencyData(
        variantMatch || productMatch,
      );
      const candidateGroupId = this.getLineGroupId(variantMatch || productMatch);

      const planId = await this.resolveSellingPlanIdForItem(oneTimeItem, {
        desiredFrequencyKey:
          normalizedDesiredFrequencyKey ||
          this.normalizeFrequencyKey(candidateFrequency.frequencyKey),
        desiredGroupId: desiredGroupId || candidateGroupId,
        fallbackPlanId: candidatePlanId,
        fallbackPlanName: candidatePlanName,
        fallbackAppId,
      });

      if (planId <= 0) continue;
      if (await this.convertLineToSellingPlan(oneTimeItem, planId)) {
        didUpdateAny = true;
      }
    }

    return didUpdateAny;
  }

  async upgradeCartItemsToAutoship() {
    const cart = await this.fetchCartStateNoCache();
    const cartItems = this.getDecoratedCartItems(cart);
    const oneTimeItems = cartItems.filter(
      (item) =>
        Number(item.quantity) > 0 &&
        !item.selling_plan_allocation &&
        !item.has_components,
    );
    if (oneTimeItems.length === 0) return false;

    const firstAutoshipItem = cartItems.find((item) => item?.selling_plan_allocation);
    const currentFrequency = this.getCurrentAutoshipIntervalFromCart({ items: cartItems });
    const autoshipContext = this.getAutoshipContext({ items: cartItems });
    const desiredFrequencyKey =
      this.normalizeFrequencyKey(currentFrequency.frequencyKey) || "month:1";
    const desiredGroupId =
      String(autoshipContext.groupId || "").trim() ||
      String(currentFrequency.groupId || "").trim();
    const fallbackAppId = String(
      autoshipContext?.config?.fallbackAppId || "5859381",
    ).trim();
    let fallbackPlanId = Number(
      firstAutoshipItem?.selling_plan_allocation?.selling_plan?.id || 0,
    );
    const fallbackPlanName =
      firstAutoshipItem?.selling_plan_allocation?.selling_plan?.name || "";

    const convertedLineKeys = [];
    let didUpdateAny = false;
    for (const lineItem of oneTimeItems) {
      const planId = await this.resolveSellingPlanIdForItem(lineItem, {
        desiredFrequencyKey,
        desiredGroupId,
        fallbackPlanId,
        fallbackPlanName,
        fallbackAppId,
      });
      if (planId <= 0) continue;
      if (fallbackPlanId <= 0) {
        fallbackPlanId = planId;
      }
      if (await this.convertLineToSellingPlan(lineItem, planId)) {
        didUpdateAny = true;
        if (lineItem?.key) {
          convertedLineKeys.push(lineItem.key);
        }
      }
    }

    const didNormalize = await this.normalizeRemainingOneTimeLinesToAutoship(
      desiredFrequencyKey,
      desiredGroupId,
      fallbackAppId,
    );
    if (!didUpdateAny && !didNormalize) return false;

    if (convertedLineKeys.length > 0) {
      await this.waitForAutoshipConversion(convertedLineKeys);
    } else {
      await this.wait(100);
    }

    return true;
  }

  async updateAutoshipItemsFrequency(frequencyKey, groupId = "") {
    const normalizedDesiredFrequencyKey = this.normalizeFrequencyKey(frequencyKey);

    const initialCart = await this.fetchCartStateNoCache().catch(() => null);
    if (!initialCart) return false;
    const initialCartItems = this.getDecoratedCartItems(initialCart);
    const autoshipContext = this.getAutoshipContext({ items: initialCartItems });
    const fallbackAppId = String(
      autoshipContext?.config?.fallbackAppId || "5859381",
    ).trim();
    const strictModeConsent = this.isStrictModeConsentEnabled(autoshipContext?.config);
    const initialAutoshipItems = initialCartItems.filter(
      (item) => Number(item?.quantity || 0) > 0 && item?.selling_plan_allocation,
    );
    if (initialAutoshipItems.length === 0) return false;
    const firstAutoshipGroupId = String(
      this.getLineGroupId(initialAutoshipItems[0]) || "",
    ).trim();
    const normalizedDesiredGroupId =
      String(autoshipContext.groupId || "").trim() ||
      String(groupId || "").trim() ||
      firstAutoshipGroupId;
    const fallbackFrequencyFromCart = this.normalizeFrequencyKey(
      this.getLineFrequencyData(initialAutoshipItems[0]).frequencyKey,
    );
    const resolvedDesiredFrequencyKey =
      normalizedDesiredFrequencyKey || fallbackFrequencyFromCart;
    if (!resolvedDesiredFrequencyKey) return false;

    let didUpdateAny = false;
    if (!strictModeConsent) {
      if (
        await this.normalizeRemainingOneTimeLinesToAutoship(
          resolvedDesiredFrequencyKey,
          normalizedDesiredGroupId,
          fallbackAppId,
        )
      ) {
        didUpdateAny = true;
      }
    }

    const cart = await this.fetchCartStateNoCache();
    const cartItems = this.getDecoratedCartItems(cart);
    const autoshipItems = cartItems.filter(
      (item) => Number(item?.quantity || 0) > 0 && item?.selling_plan_allocation,
    );
    if (autoshipItems.length === 0) return false;
    for (const item of autoshipItems) {
      const currentPlanId = Number(item?.selling_plan_allocation?.selling_plan?.id || 0);
      const currentPlanName =
        item?.selling_plan_allocation?.selling_plan?.name || "";
      const sellingPlanId = await this.resolveSellingPlanIdForItem(item, {
        desiredFrequencyKey: resolvedDesiredFrequencyKey,
        desiredGroupId: normalizedDesiredGroupId,
        fallbackPlanId: currentPlanId,
        fallbackPlanName: currentPlanName,
        fallbackAppId,
      });

      if (sellingPlanId <= 0 || sellingPlanId === currentPlanId) continue;
      if (await this.convertLineToSellingPlan(item, sellingPlanId)) {
        didUpdateAny = true;
      }
    }

    if (!strictModeConsent) {
      if (
        await this.normalizeRemainingOneTimeLinesToAutoship(
          resolvedDesiredFrequencyKey,
          normalizedDesiredGroupId,
          fallbackAppId,
        )
      ) {
        didUpdateAny = true;
      }
    }

    const finalCart = await this.fetchCartStateNoCache().catch(() => null);
    const finalAutoshipItems = (finalCart?.items || []).filter(
      (item) => Number(item?.quantity || 0) > 0 && item?.selling_plan_allocation,
    );
    const allMatchedDesired =
      finalAutoshipItems.length > 0 &&
      finalAutoshipItems.every((lineItem) => {
        const lineFrequency = this.getLineFrequencyData(lineItem);
        const normalizedLineFrequency = this.normalizeFrequencyKey(
          lineFrequency.frequencyKey,
        );
        return (
          this.isFrequencyKeyMatch(
            normalizedLineFrequency,
            resolvedDesiredFrequencyKey,
          ) &&
          this.isGroupIdMatch(lineFrequency.groupId, normalizedDesiredGroupId)
        );
      });

    return didUpdateAny || allMatchedDesired;
  }

  runAutoshipUpgrade(button) {
    this.isAutoshipUpdating = true;
    this.classList.add("is-autoship-loading");
    button.disabled = true;

    return this.upgradeCartItemsToAutoship()
      .then((didUpdate) => {
        const persistUpgradeState = didUpdate
          ? this.setAutoshipUpgradeState(true, "one_time_upgrade").catch((error) => {
              console.error(error);
            })
          : Promise.resolve();

        return persistUpgradeState.then(() => {
          if (!didUpdate) {
            return this.refreshCartDrawer();
          }
          return this.refreshCartDrawer()
            .then(() => this.wait(80))
            .then(() => this.refreshCartDrawer())
            .then(() => {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: "cart-drawer-upgrade-autoship",
              });
            });
        });
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.isAutoshipUpdating = false;
        this.classList.remove("is-autoship-loading");
        if (button && button.isConnected) {
          button.disabled = false;
        }
      });
  }

  runAutoshipFrequencyUpdate(selectElement, frequencyKey, groupId = "") {
    this.rememberFrequencySelection(frequencyKey, groupId);
    this.isAutoshipUpdating = true;
    this.classList.add("is-autoship-loading");
    selectElement.disabled = true;

    return this.updateAutoshipItemsFrequency(frequencyKey, groupId)
      .then((didUpdate) =>
        this.refreshCartDrawer().then(() => {
          if (!didUpdate) return;
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: "cart-drawer-autoship-frequency",
          });
        }),
      )
      .catch((error) => {
        this.clearFrequencySelection();
        console.error(error);
      })
      .finally(() => {
        this.isAutoshipUpdating = false;
        this.classList.remove("is-autoship-loading");
        if (selectElement && selectElement.isConnected) {
          selectElement.disabled = false;
        }
      });
  }

  runRecommendationUpdate(controlButton, action) {
    this.isRecoUpdating = true;
    this.classList.add("is-reco-loading");

    const task =
      action === "plus"
        ? this.incrementRecommendationItem(controlButton)
        : this.decrementRecommendationItem(controlButton);

    return task
      .then(async (taskResult) => {
        const didUpdate = Boolean(taskResult?.didUpdate);
        if (!didUpdate) return;
        let didBundleSync = false;
        if (action === "plus" && window.AvCartSync?.syncAfterAddonPlus) {
          try {
            const syncPayload = {
              source: "cart-drawer-reco-plus",
              addedVariantId: Number(
                taskResult?.addedVariantId || controlButton?.dataset?.variantId || 0,
              ),
              addedProductHandle: String(
                taskResult?.addedProductHandle ||
                  controlButton?.dataset?.productHandle ||
                  "",
              ).trim(),
              addedSellingPlanId: Number(taskResult?.addedSellingPlanId || 0),
              purchaseMode: this.normalizePurchaseMode(taskResult?.purchaseMode, ""),
            };
            const syncResult = await window.AvCartSync.syncAfterAddonPlus(
              syncPayload,
            );
            didBundleSync = Boolean(syncResult?.changed);
            this.debugLog("Drawer plus -> AvCartSync.syncAfterAddonPlus", {
              payload: syncPayload,
              result: syncResult,
            });
          } catch (error) {
            this.debugLog("Drawer plus -> AvCartSync.syncAfterAddonPlus error", {
              source: "cart-drawer-reco-plus",
              error: error?.message || String(error),
            });
            console.error(error);
          }
        }
        if (action === "minus" && window.AvCartSync?.syncNow) {
          try {
            await window.AvCartSync.syncNow({
              source: "cart-drawer-reco-minus",
              trigger: "remove",
              changedLineKey: String(taskResult?.changedLineKey || "").trim(),
              changedBundleId: String(taskResult?.changedBundleId || "").trim(),
            });
          } catch (error) {
            this.debugWarn("Drawer minus -> AvCartSync.syncNow failed", {
              error: error?.message || String(error),
            });
            console.error(error);
          }
        } else if (action === "minus" && !didBundleSync) {
          const changedBundleId = String(taskResult?.changedBundleId || "").trim();
          if (!changedBundleId) {
            return this.refreshCartDrawer().then(() => {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: "cart-drawer-recommendations",
              });
            });
          }
          try {
            await this.cleanupOrphanBundleGroups({
              scopeBundleIds: [changedBundleId],
            });
          } catch (error) {
            console.error(error);
          }
        }
        return this.refreshCartDrawer().then(() => {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: "cart-drawer-recommendations",
          });
        });
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.isRecoUpdating = false;
        this.classList.remove("is-reco-loading");
        controlButton.disabled = false;
      });
  }

  clearReserveTimer() {
    if (!this.reserveTimerInterval) return;
    window.clearInterval(this.reserveTimerInterval);
    this.reserveTimerInterval = undefined;
  }

  formatReserveTime(totalSeconds) {
    const safeSeconds = Number.isFinite(totalSeconds)
      ? Math.max(0, totalSeconds)
      : 0;
    const minutes = Math.floor(safeSeconds / 60);
    const seconds = safeSeconds % 60;
    return `${minutes}:${String(seconds).padStart(2, "0")}`;
  }

  setupReserveTimer() {
    this.clearReserveTimer();
    const reserveRoot = this.querySelector("[data-cart-reserve]");
    const timerElement = reserveRoot?.querySelector("[data-cart-reserve-timer]");
    if (!reserveRoot || !timerElement) return;

    const reserveSecondsRaw = Number(reserveRoot.dataset.reserveSeconds);
    const reserveSeconds =
      Number.isFinite(reserveSecondsRaw) && reserveSecondsRaw > 0
        ? reserveSecondsRaw
        : 453;
    const storageKey = "avanelle-cart-reserve-expires-at";

    let expiresAt = 0;
    try {
      expiresAt = Number(window.sessionStorage.getItem(storageKey));
    } catch (e) {
      expiresAt = 0;
    }

    const now = Date.now();
    if (!Number.isFinite(expiresAt) || expiresAt <= now) {
      expiresAt = now + reserveSeconds * 1000;
      try {
        window.sessionStorage.setItem(storageKey, String(expiresAt));
      } catch (e) {
        // Session storage might be unavailable in private mode.
      }
    }

    const tick = () => {
      let remaining = Math.floor((expiresAt - Date.now()) / 1000);
      if (remaining <= 0) {
        expiresAt = Date.now() + reserveSeconds * 1000;
        try {
          window.sessionStorage.setItem(storageKey, String(expiresAt));
        } catch (e) {
          // Ignore write failures and keep the timer in-memory.
        }
        remaining = reserveSeconds;
      }
      timerElement.textContent = this.formatReserveTime(remaining);
    };

    tick();
    this.reserveTimerInterval = window.setInterval(tick, 1000);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define("cart-drawer", CartDrawerSection);

const getCartDrawerController = () => {
  const drawer = document.querySelector("cart-drawer");
  if (!drawer) return null;
  if (
    typeof drawer.runAutoshipUpgrade !== "function" ||
    typeof drawer.runAutoshipFrequencyUpdate !== "function" ||
    typeof drawer.runRecommendationUpdate !== "function"
  ) {
    return null;
  }
  return drawer;
};

const bindMainCartActionFallback = () => {
  if (window.__avMainCartActionFallbackBound) return;
  window.__avMainCartActionFallbackBound = true;

  document.addEventListener("click", (event) => {
    if (event.defaultPrevented) return;

    const plusButton = event.target.closest("[data-cart-reco-plus]");
    const minusButton = event.target.closest("[data-cart-reco-minus]");
    const controlButton = plusButton || minusButton;
    if (controlButton) {
      const controller = getCartDrawerController();
      if (!controller) return;
      event.preventDefault();
      if (controller.isRecoUpdating || controlButton.disabled) return;
      controlButton.disabled = true;
      controller.runRecommendationUpdate(
        controlButton,
        plusButton ? "plus" : "minus",
      );
      return;
    }

    const navButton = event.target.closest("[data-cart-reco-scroll]");
    if (navButton) {
      event.preventDefault();
      const direction = navButton.dataset.cartRecoScroll === "prev" ? -1 : 1;
      const recoRoot = navButton.closest(".av-cart-reco, .av-main-cart-flavors");
      const track = recoRoot?.querySelector("[data-cart-reco-track]");
      if (!track) return;
      const step = Math.max(Math.floor(track.clientWidth * 0.82), 180);
      track.scrollBy({
        left: direction * step,
        behavior: "smooth",
      });
      return;
    }

    const upgradeButton = event.target.closest("[data-cart-upgrade-autoship]");
    if (upgradeButton) {
      const controller = getCartDrawerController();
      if (!controller) return;
      event.preventDefault();
      if (controller.isAutoshipUpdating) return;
      controller.runAutoshipUpgrade(upgradeButton);
    }
  });

  document.addEventListener("change", (event) => {
    if (event.defaultPrevented) return;

    const frequencySelect = event.target.closest("[data-cart-autoship-frequency]");
    if (!frequencySelect) return;

    const controller = getCartDrawerController();
    if (!controller) return;

    const frequencyKey = controller.normalizeFrequencyKey(frequencySelect.value);
    if (!frequencyKey) return;
    const selectedOption =
      frequencySelect.options[frequencySelect.selectedIndex] || null;
    const groupId =
      String(frequencySelect.dataset.currentPlanGroupId || "").trim() ||
      String(selectedOption?.dataset?.groupId || "").trim();
    if (controller.isAutoshipUpdating) return;

    controller.runAutoshipFrequencyUpdate(
      frequencySelect,
      frequencyKey,
      groupId,
    );
  });
};

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", bindMainCartActionFallback, {
    once: true,
  });
} else {
  bindMainCartActionFallback();
}

class CartDrawerItems extends CartItems {
  onCartUpdate() {
    const cartDrawer = document.querySelector("cart-drawer");
    if (cartDrawer && typeof cartDrawer.refreshCartDrawer === "function") {
      cartDrawer.refreshCartDrawer();
      return;
    }

    super.onCartUpdate();
  }

  getSectionsToRender() {
    return [
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".drawer__inner",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
    ];
  }
}

customElements.define("cart-drawer-items", CartDrawerItems);
